/**
 * Cache-busting version token for blob-storage image URLs.
 *
 * Vehicle and user images are overwritten *in place* in Azure Blob Storage, so
 * their public URLs never change. The blobs carry no `Cache-Control` header,
 * which makes browsers fall back to heuristic caching and keep serving stale
 * copies for hours/days after an image is replaced. Appending `?v=<version>`
 * changes the URL the browser caches by, forcing a one-time refetch. Azure
 * ignores unknown query params on public blobs, so this is safe.
 *
 * BUMP THIS whenever images are replaced in storage (e.g. a new vehicle-image
 * batch upload). Any change to the value invalidates every cached image.
 */
export const IMAGE_VERSION = "2026-09-02";

/**
 * Appends the {@link IMAGE_VERSION} cache-busting token to a single image URL.
 * Falsy values (undefined / null / empty string) are returned unchanged, and
 * the input's nullability is preserved in the return type.
 */
export function withImageVersion<T extends string | null | undefined>(
	url: T
): T {
	if (!url) return url;
	const separator = url.includes("?") ? "&" : "?";
	return `${url}${separator}v=${IMAGE_VERSION}` as T;
}

interface VersionableImageUrls {
	thumbnail300x300Url?: string | null;
	square1000x1000Url?: string | null;
	wide1600x1000Url?: string | null;
}

/**
 * Returns a copy of an `imageUrls` object with the cache-busting token applied
 * to each of its size variants. Non-image fields are preserved; `undefined`
 * input is returned unchanged.
 */
export function withImageVersions<T extends VersionableImageUrls>(urls: T): T;
export function withImageVersions<T extends VersionableImageUrls>(
	urls: T | undefined
): T | undefined;
export function withImageVersions<T extends VersionableImageUrls>(
	urls: T | undefined
): T | undefined {
	if (!urls) return urls;
	return {
		...urls,
		thumbnail300x300Url: withImageVersion(urls.thumbnail300x300Url),
		square1000x1000Url: withImageVersion(urls.square1000x1000Url),
		wide1600x1000Url: withImageVersion(urls.wide1600x1000Url),
	} as T;
}
